import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

// Admin defaults for HOW canvas blocks compute, at three scopes: the platform
// default that applies to every account, one account's override, or one
// workspace's override (a second, cascading picker under the account).
// Modelled on DefaultSourcesManager, which solves the same shape for BigQuery
// sync — including the "Global default (all accounts)" entry at the top of the
// scope picker. Since 2026-08 this panel is the ONLY write surface: the
// customer-facing workspace-modal controls were removed.
//
// Full precedence at run time is:
//   workspace ?? account ?? template ?? platform default ?? built-in
//
// Two of these three change ANSWERS, not preferences — the warning is
// deliberate. The canvas engine was aligned to the workflow agent because the
// two disagreed on the same data and the workflow's answers were the
// trustworthy ones; the built-in defaults are that alignment.
//
// MODELS / EFFORTS / COMPUTE_WARNING are exported because CanvasPublishManager
// (the template exec-override form) renders the same vocabulary. One
// definition, so the surfaces cannot drift.

// MUST stay a subset of the server's catalog (chat/modelCatalog.js
// CHAT_PROVIDERS, gated by canvasExecConfigService#isValidExecValue): PUT
// /admin/canvas-exec-config answers an off-catalog pair with 400 BAD_VALUE, so
// anything listed here that the catalog does not carry is a button that cannot
// work. Claude and Gemini were dropped on 2026-09-04 with the chat's return to
// ChatGPT-only; stored pins on them resolve to the default via RETIRED_MODELS.
export const MODELS = [
  { provider: 'chatgpt', model: 'gpt-5-mini', label: 'ChatGPT · GPT-5 Mini (built-in default)' },
  { provider: 'chatgpt', model: 'gpt-5', label: 'ChatGPT · GPT-5 (slower, ~5x the token cost)' },
];

export const EFFORTS = ['omit', 'none', 'minimal', 'low', 'medium', 'high'];

export const COMPUTE_WARNING = 'Changes how answers are computed. The default matches the workflow agent, which is the reference implementation.';

export const effortLabel = (v) => (v === 'omit' ? 'Off — send no reasoning' : v);

export const modelLabel = (provider, model) =>
  MODELS.find((m) => m.provider === provider && m.model === model)?.label || `${provider} · ${model}`;

export const deliveryLabel = (v) => (v === 'inline' ? 'Inline in the prompt' : v === 'file' ? 'Attached file' : 'Automatic');

// The settings a scope pins, as human-readable lines. Only what that scope
// actually set — an unpinned key inherits, and is not this card's business.
export const summarizeOverrides = (o = {}) => {
  const out = [];
  if (o.model) out.push(`Model: ${modelLabel(o.provider, o.model)}`);
  if (o.reasoningEffort) out.push(`Reasoning: ${effortLabel(o.reasoningEffort)}`);
  if (o.dataDelivery) out.push(`Data delivery: ${deliveryLabel(o.dataDelivery)}`);
  return out;
};

const GLOBAL_SCOPE = '';
const INHERIT = '__inherit__';
const EXEC_KEYS = ['modelPair', 'reasoningEffort', 'dataDelivery'];

const fmtDate = (d) => {
  if (!d) return '';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const s = {
  card: { border: '1px solid var(--border)', borderRadius: '10px', padding: '20px 22px', background: '#fbfcfe' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' },
  intro: { fontSize: '12.5px', color: 'var(--text-secondary)', margin: '0 0 14px', lineHeight: 1.6 },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '5px', display: 'block' },
  select: { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: '#fff', marginBottom: '14px' },
  warn: { marginTop: '-8px', marginBottom: '14px', padding: '8px 10px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '11.5px' },
  hint: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '-10px', marginBottom: '14px' },
  savedWrap: { marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' },
  savedCard: (active) => ({
    border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    borderRadius: '9px', padding: '11px 13px', marginBottom: '9px', background: '#fff',
  }),
  savedHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' },
  savedName: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' },
  savedMeta: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' },
  savedList: { margin: '7px 0 0', paddingLeft: '16px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.7 },
  actions: { display: 'flex', gap: '7px', flexShrink: 0 },
  miniBtn: { padding: '4px 11px', borderRadius: '7px', border: '1px solid var(--border)', background: '#fff', color: 'var(--text-primary)', fontWeight: 600, fontSize: '11.5px', cursor: 'pointer' },
  miniDanger: { padding: '4px 11px', borderRadius: '7px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: '11.5px', cursor: 'pointer' },
  tag: { display: 'inline-block', padding: '1px 7px', borderRadius: '20px', background: '#eef2ff', color: '#4338ca', fontSize: '10px', fontWeight: 700, marginLeft: '7px', verticalAlign: 'middle' },
  empty: { fontSize: '12.5px', color: 'var(--text-muted)', margin: '4px 0 0' },
};

/**
 * @param {{shopKey: string, name: string}[]} stores from the parent's
 *   /admin/stores call — passed in rather than fetched again, since the panel
 *   this lives inside already has them.
 */
export default function CanvasExecutionManager({ stores = [] }) {
  const [scope, setScope] = useState(GLOBAL_SCOPE); // '' = platform default, else a shopKey
  const [wsScope, setWsScope] = useState(''); // '' = whole account, else a workspaceId (only with scope set)
  const [layers, setLayers] = useState(null); // { global, shop, workspace?, workspaces? } raw override maps
  const [busy, setBusy] = useState('');
  // Every scope that pins something. Without it this panel can only answer
  // "what does the account I picked have set?", so auditing means clicking
  // through the entire store list.
  const [saved, setSaved] = useState(null);

  const nameOf = useCallback(
    (key) => stores.find((st) => st.shopKey === key)?.name || key,
    [stores],
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/canvas-exec-config', {
        params: wsScope ? { workspaceId: wsScope } : scope ? { shop: scope } : {},
      });
      setLayers(data);
    } catch {
      toast.error('Failed to load canvas execution settings');
    }
  }, [scope, wsScope]);
  useEffect(() => { load(); }, [load]);

  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/canvas-exec-config/saved');
      setSaved(data);
    } catch {
      // Non-fatal: the editor above still works without the audit list, and a
      // toast here would fire on every save.
      setSaved({ global: null, shops: [], workspaces: [], failed: true });
    }
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const save = async (key, value) => {
    setBusy(key);
    try {
      const { data } = await api.put('/admin/canvas-exec-config', {
        scope: wsScope ? 'workspace' : scope ? 'shop' : 'global',
        shop: !wsScope && scope ? scope : undefined,
        workspaceId: wsScope || undefined,
        key,
        value,
      });
      // Merge rather than replace: the PUT response carries the override maps
      // but not the account's `workspaces` picker list from the GET.
      setLayers((prev) => ({ ...prev, ...data }));
      toast.success('Saved');
      await loadSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setBusy('');
    }
  };

  // Clearing is three writes because each key is independently settable — there
  // is no "delete the doc" verb, and adding one would let a scope whose doc is
  // gone read differently from one whose keys are all unset. Sequential, so a
  // rejection stops rather than racing the rest; either way both views reload,
  // since a partial clear must not leave the cards claiming the old state.
  const clearScope = async ({ shopKey = '', workspaceId = '', label }) => {
    if (!window.confirm(`Clear every canvas execution setting for ${label}? It will inherit again.`)) return;
    const tid = toast.loading('Clearing…');
    try {
      for (const key of EXEC_KEYS) {
        // eslint-disable-next-line no-await-in-loop
        await api.put('/admin/canvas-exec-config', {
          scope: workspaceId ? 'workspace' : shopKey ? 'shop' : 'global',
          shop: !workspaceId && shopKey ? shopKey : undefined,
          workspaceId: workspaceId || undefined,
          key,
          value: null,
        });
      }
      toast.success('Cleared', { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear', { id: tid });
    } finally {
      await Promise.all([load(), loadSaved()]);
    }
  };

  if (!layers) return <div style={s.card}>Loading…</div>;

  // The layer this scope EDITS, and the ones it falls back to when it sets
  // nothing. A workspace inherits per key through shop then global (shallow
  // merge is safe: modelPair always writes/clears provider+model together).
  // At global scope there is no lower layer but the built-in.
  const own = wsScope ? (layers.workspace || {}) : scope ? layers.shop : layers.global;
  const inherited = wsScope ? { ...layers.global, ...layers.shop } : scope ? layers.global : {};
  const workspaces = layers.workspaces || [];

  const pairValue = own.model ? `${own.provider}|${own.model}` : INHERIT;
  const inheritedPairLabel = inherited.model
    ? MODELS.find((m) => m.model === inherited.model)?.label || inherited.model
    : MODELS[0].label;

  const metaLine = (row) => [fmtDate(row.updatedAt) && `Updated ${fmtDate(row.updatedAt)}`, row.updatedBy]
    .filter(Boolean).join(' · ');

  const savedCard = ({ key, title, tag, meta, overrides, active, onEdit, onClear, note }) => (
    <div key={key} style={s.savedCard(active)}>
      <div style={s.savedHead}>
        <div>
          <div style={s.savedName}>
            {title}
            {tag && <span style={s.tag}>{tag}</span>}
          </div>
          {meta && <div style={s.savedMeta}>{meta}</div>}
        </div>
        <div style={s.actions}>
          {onEdit && (
            <button type="button" className="btn-height" style={s.miniBtn} onClick={onEdit}>
              {active ? 'Editing' : 'Edit'}
            </button>
          )}
          {onClear && (
            <button type="button" className="btn-height" style={s.miniDanger} onClick={() => onClear()}>Clear</button>
          )}
        </div>
      </div>
      <ul style={s.savedList}>
        {summarizeOverrides(overrides).map((line) => <li key={line}>{line}</li>)}
      </ul>
      {note && <div style={s.savedMeta}>{note}</div>}
    </div>
  );

  const nothingSaved = saved && !saved.failed
    && !saved.global && !(saved.shops || []).length && !(saved.workspaces || []).length;

  return (
    <div style={s.card}>
      <div style={s.sectionTitle}>Canvas execution</div>
      <p style={s.intro}>
        How canvas blocks compute. A template can pin its own (below), and an account or
        workspace can override both — so this is the floor, not the last word.
      </p>

      <span style={s.label}>Scope</span>
      <select style={s.select} value={scope} onChange={(e) => { setLayers(null); setWsScope(''); setScope(e.target.value); }}>
        <option value={GLOBAL_SCOPE}>Global default (all accounts)</option>
        {stores.map((st) => <option key={st.shopKey} value={st.shopKey}>{st.name}</option>)}
      </select>

      {scope && (
        <>
          <span style={s.label}>Workspace</span>
          <select style={s.select} value={wsScope} onChange={(e) => { setLayers(null); setWsScope(e.target.value); }}>
            <option value="">Whole account</option>
            {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
          </select>
        </>
      )}

      <span style={s.label}>Model</span>
      <select style={s.select} value={pairValue} disabled={busy === 'modelPair'}
        onChange={(e) => {
          if (e.target.value === INHERIT) return save('modelPair', null);
          const [provider, model] = e.target.value.split('|');
          return save('modelPair', { provider, model });
        }}>
        <option value={INHERIT}>
          {scope ? `Inherit — ${inheritedPairLabel}` : `Built-in default — ${MODELS[0].label}`}
        </option>
        {MODELS.map((m) => (
          <option key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label}</option>
        ))}
      </select>
      <div style={s.hint}>
        Applies to every agent block on every canvas in scope, so it multiplies spend accordingly.
      </div>

      <span style={s.label}>Reasoning effort</span>
      <select style={s.select} value={own.reasoningEffort || INHERIT} disabled={busy === 'reasoningEffort'}
        onChange={(e) => save('reasoningEffort', e.target.value === INHERIT ? null : e.target.value)}>
        <option value={INHERIT}>
          {scope
            ? `Inherit — ${effortLabel(inherited.reasoningEffort || 'omit')}`
            : `Built-in default — ${effortLabel('omit')}`}
        </option>
        {EFFORTS.map((v) => <option key={v} value={v}>{effortLabel(v)}</option>)}
      </select>
      <div style={s.warn}>{COMPUTE_WARNING}</div>

      <span style={s.label}>Data delivery</span>
      <select style={s.select} value={own.dataDelivery || INHERIT} disabled={busy === 'dataDelivery'}
        onChange={(e) => save('dataDelivery', e.target.value === INHERIT ? null : e.target.value)}>
        <option value={INHERIT}>
          {scope
            ? `Inherit — ${deliveryLabel(inherited.dataDelivery)}`
            : 'Built-in default — Automatic'}
        </option>
        <option value="auto">Automatic — the planner decides per run (small datasets inline, large ones attached)</option>
        <option value="file">Attached file — always computed over the full dataset (the workflow agent's reference behaviour)</option>
        <option value="inline">Inline in the prompt</option>
      </select>
      <div style={s.warn}>
        {COMPUTE_WARNING} Automatic inlines datasets up to 300 rows / 64 KB and attaches anything larger. Inline always sends rows in the prompt and skips the sandbox, so figures are read rather than computed.
      </div>

      <div style={s.savedWrap}>
        <div style={s.sectionTitle}>Saved configurations</div>
        {!saved && <p style={s.empty}>Loading…</p>}
        {saved?.failed && <p style={s.empty}>Couldn&apos;t load the saved list. The editor above still works.</p>}
        {nothingSaved && (
          <p style={s.empty}>Nothing is configured anywhere — every canvas runs on the built-in defaults.</p>
        )}

        {saved?.global && savedCard({
          key: 'global',
          title: 'Global default',
          tag: 'ALL ACCOUNTS',
          meta: metaLine(saved.global),
          overrides: saved.global.overrides,
          active: scope === GLOBAL_SCOPE,
          onEdit: () => { setLayers(null); setWsScope(''); setScope(GLOBAL_SCOPE); },
          onClear: () => clearScope({ label: 'the global default' }),
        })}

        {(saved?.shops || []).map((row) => savedCard({
          key: row.shop,
          title: nameOf(row.shop),
          tag: 'ACCOUNT',
          meta: metaLine(row),
          overrides: row.overrides,
          active: scope === row.shop && !wsScope,
          onEdit: () => { setLayers(null); setWsScope(''); setScope(row.shop); },
          onClear: () => clearScope({ shopKey: row.shop, label: nameOf(row.shop) }),
        }))}

        {/* Workspace layers BEAT the account rows above. Editing jumps both
            pickers to that workspace; a deleted workspace's orphaned config
            can't be edited but can (and should) still be cleared. */}
        {(saved?.workspaces || []).map((row) => savedCard({
          key: row.workspaceId,
          title: row.name || 'Deleted workspace',
          tag: 'WORKSPACE',
          meta: [row.shop ? nameOf(row.shop) : null, metaLine(row)].filter(Boolean).join(' · '),
          overrides: row.overrides,
          active: wsScope === row.workspaceId,
          onEdit: row.shop
            ? () => { setLayers(null); setScope(row.shop); setWsScope(row.workspaceId); }
            : undefined,
          onClear: () => clearScope({
            workspaceId: row.workspaceId,
            label: row.name ? `workspace "${row.name}"` : 'this deleted workspace',
          }),
        }))}
      </div>
    </div>
  );
}
